import { useDisclaimers } from '@/hooks/queries/disclaimers';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { SOUL_MAP_PDF_FILENAME } from '@/hooks/useSoulMapDocumentDownload';
import { useSoulMapReadyNotice } from '@/hooks/useSoulMapReadyNotice';
import ChatPage from '@/pages/chat-page/chat-page';
import { routeConfig } from '@/routes/route-config';
import { routeRendersPage } from '@/routes/route-page';
import PracticeService from '@/services/practice-service';
import { useUserStore } from '@/store/use-user-store';
import { isSoulMapMode } from '@/utils/app-mode';
import { useProfileDisplayName } from '@/utils/profile-variables';
import {
  markSoulMapReadyModalShown,
  shouldShowSoulMapReadyModal,
  type SoulMapReadyItem,
} from '@/utils/soul-map-ready';
import { isSoulMapAppRoute } from '@/utils/soulmap-auth';
import { getSoulMapDownloadErrorMessage } from '@/utils/soulmap-document-download';
import { Button, Modal, ModalContent, ModalHeader } from '@nextui-org/react';
import { useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';

/** Lets the backend record the download before the status is re-read. */
const REFRESH_AFTER_DOWNLOAD_MS = 1200;

/**
 * One-time "Your Soul Map is ready" notice, shown on any page once a PDF
 * finishes. Renders nothing unless the tenant has the `soul_map_ready_seen`
 * user variable. Several unseen PDFs show the newest one; acknowledging it
 * stores its `generated_at`, which covers the older ones too.
 */
const SoulMapReadyModal = () => {
  const queryClient = useQueryClient();
  const { tenantSlug } = useActiveTenant();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // The Soul Map app runs its chats under /soulmap, like useInitializePractice.
  const chatBasePath =
    isSoulMapMode() || isSoulMapAppRoute(pathname) ? 'soulmap' : 'practices';
  const onChatPage = routeRendersPage(pathname, routeConfig, ChatPage);
  const { data, sessionKey, acknowledge } = useSoulMapReadyNotice();
  const hasAcceptedDisclaimer = useUserStore(
    (state) => state.hasAcceptedDisclaimer
  );
  const { data: disclaimersData, isLoading: isLoadingDisclaimers } =
    useDisclaimers();
  const isDisclaimerPending =
    isLoadingDisclaimers ||
    (Boolean(disclaimersData?.results?.[0]) && !hasAcceptedDisclaimer);

  // A snapshot, so a re-check while the modal is open does not change it.
  const [items, setItems] = useState<SoulMapReadyItem[] | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const acknowledgedRef = useRef(false);

  useEffect(() => {
    // Never over a chat. Nothing is marked or acknowledged, so it still shows
    // on the next non-chat page.
    if (items || isDisclaimerPending || onChatPage) return;
    if (shouldShowSoulMapReadyModal(sessionKey, data)) {
      markSoulMapReadyModalShown(sessionKey, data.items);
      acknowledgedRef.current = false;
      setItems(data.items);
    }
  }, [data, isDisclaimerPending, items, onChatPage, sessionKey]);

  if (!items) return null;

  // Items are sorted newest first.
  const item = items[0];

  const acknowledgeOnce = () => {
    if (acknowledgedRef.current) return;
    acknowledgedRef.current = true;
    void acknowledge(items);
  };

  const close = () => {
    acknowledgeOnce();
    setItems(null);
  };

  const download = async () => {
    if (!item.downloadUrl || isDownloading) return;
    acknowledgeOnce();
    setIsDownloading(true);
    try {
      const result = await PracticeService.downloadSoulMapDocument(
        item.downloadUrl,
        SOUL_MAP_PDF_FILENAME,
        { practiceId: item.practiceId }
      );
      if (result.mode === 'blob') {
        saveAs(result.blob, result.filename);
      }
      window.setTimeout(
        () =>
          void queryClient.invalidateQueries({
            queryKey: ['soul-map-documents', String(item.practiceId)],
          }),
        REFRESH_AFTER_DOWNLOAD_MS
      );
      setItems(null);
    } catch (error) {
      console.error('Soul Map PDF download failed:', error);
      toast.error(getSoulMapDownloadErrorMessage(error));
    } finally {
      setIsDownloading(false);
    }
  };

  const chatPath = `/${tenantSlug}/${chatBasePath}/${item.practiceId}/run/?action=${encodeURIComponent(item.practiceName)}&id=${item.practiceId}`;

  return (
    <SoulMapReadyDialog
      canGoToChat={Boolean(tenantSlug)}
      hasDownload={Boolean(item.downloadUrl)}
      isDownloading={isDownloading}
      onClose={close}
      onDownload={() => void download()}
      // Navigate explicitly: closing unmounts the modal before a link's
      // click would fire, so a link would never navigate.
      onGoToChat={() => {
        close();
        navigate(chatPath);
      }}
    />
  );
};

/**
 * The modal itself. Mounted only while it is open, so the profile-name and
 * media-query hooks add no work to pages where it never shows.
 */
const SoulMapReadyDialog = ({
  canGoToChat,
  hasDownload,
  isDownloading,
  onClose,
  onDownload,
  onGoToChat,
}: {
  canGoToChat: boolean;
  hasDownload: boolean;
  isDownloading: boolean;
  onClose: () => void;
  onDownload: () => void;
  onGoToChat: () => void;
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { firstName: profileFirstName, fullName } = useProfileDisplayName();
  // resolveProfileFirstName falls back to "Guest"; the title reads better without it.
  const firstName =
    profileFirstName && profileFirstName !== 'Guest' ? profileFirstName : null;

  return (
    <Modal
      isOpen
      onClose={onClose}
      hideCloseButton
      size="xl"
      placement="center"
      // Phones: the modal can be taller than the screen, so let it scroll.
      scrollBehavior={isMobile ? 'outside' : 'normal'}
      classNames={{
        // Above the mobile bottom nav (z-[100]), which otherwise shows over the backdrop.
        backdrop: 'z-[110]',
        wrapper: 'z-[110]',
        base: 'overflow-hidden',
      }}
      style={{ borderRadius: 24, margin: isMobile ? '16px 12px' : undefined }}
    >
      <ModalContent>
        <SoulMapHero
          isMobile={isMobile}
          documentName={firstName ? fullName : null}
          onClose={onClose}
        />
        <div
          className="flex flex-col items-center text-center"
          style={{
            padding: isMobile ? '24px 24px 28px' : '32px 44px 36px',
            color: COLORS.ink,
          }}
        >
          <ModalHeader className="flex flex-col items-center p-0">
            <span
              className="font-bold uppercase"
              style={{
                marginBottom: 10,
                fontSize: 12,
                letterSpacing: '0.14em',
                color: COLORS.accent,
              }}
            >
              Your Soul Map
            </span>
            <h2
              style={{
                margin: isMobile ? '0 0 12px' : '0 0 14px',
                fontFamily: SERIF_FONT,
                fontWeight: 400,
                fontSize: isMobile ? 28 : 34,
                lineHeight: isMobile ? '34px' : '42px',
              }}
            >
              {firstName
                ? `${firstName}, it’s ready for you`
                : 'It’s ready for you'}
            </h2>
          </ModalHeader>
          <p
            style={{
              margin: 0,
              maxWidth: 470,
              fontSize: 16,
              lineHeight: '26px',
              color: COLORS.body,
            }}
          >
            Everything you shared in your Soul Map conversation has been brought
            together in one personal document, written for you across twelve
            sections. Find a quiet moment to open it; it’s yours to keep, print
            and come back to.
          </p>

          <div
            className="flex w-full flex-col items-stretch justify-center gap-3 sm:w-auto sm:flex-row sm:items-center"
            style={{ marginTop: 28 }}
          >
            {hasDownload && (
              <Button
                radius="full"
                isLoading={isDownloading}
                startContent={!isDownloading && <DownloadIcon />}
                onPress={onDownload}
                className="font-bold"
                style={{
                  height: 52,
                  padding: '0 28px',
                  fontSize: 16,
                  background: COLORS.ink,
                  color: '#ffffff',
                }}
              >
                Download my Soul Map
              </Button>
            )}
            {canGoToChat && (
              <Button
                radius="full"
                variant="bordered"
                onPress={onGoToChat}
                className="font-semibold"
                style={{
                  height: 52,
                  padding: '0 24px',
                  fontSize: 16,
                  borderWidth: 1,
                  borderColor: COLORS.border,
                  color: COLORS.ink,
                }}
              >
                Go to my Soul Map chat
              </Button>
            )}
          </div>

          <p
            style={{
              margin: '22px 0 0',
              fontSize: 13,
              lineHeight: '20px',
              color: COLORS.muted,
            }}
          >
            You’ll also find it in your Soul Map chat whenever you want it.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

/** Loaded in index.html; the Soul Map chat card uses it too. */
const SERIF_FONT = 'Fraunces, Georgia, serif';

/** Design values (LiFT-SoulMapReadyModal), kept inline so they never depend on generated CSS. */
const COLORS = {
  ink: '#17161c',
  body: '#52525b',
  muted: '#68656f',
  border: '#e5e7eb',
  accent: '#b8533f',
  coral: '#ee8571',
  violet: '#a594e8',
  heroBackground: '#f7efe9',
  paper: '#fffdfb',
  paperLine: '#efe9e2',
};

const SoulMapHero = ({
  isMobile,
  documentName,
  onClose,
}: {
  isMobile: boolean;
  documentName: string | null;
  onClose: () => void;
}) => (
  <div
    className="relative flex items-center justify-center overflow-hidden"
    style={{ height: isMobile ? 220 : 268, background: COLORS.heroBackground }}
  >
    <Constellation />

    <div
      aria-hidden="true"
      className="relative flex flex-col"
      style={{
        width: 168,
        height: 216,
        gap: 8,
        padding: '20px 18px',
        boxSizing: 'border-box',
        borderRadius: 6,
        background: COLORS.paper,
        boxShadow:
          '0 2px 4px rgba(23, 22, 28, 0.06), 0 18px 40px rgba(184, 83, 63, 0.22)',
        transform: isMobile ? 'rotate(-3deg) scale(0.8)' : 'rotate(-3deg)',
      }}
    >
      <div
        style={{
          height: 3,
          width: '100%',
          borderRadius: 2,
          background: COLORS.coral,
        }}
      />
      <p
        className="font-bold uppercase"
        style={{
          margin: '6px 0 0',
          fontSize: 9,
          letterSpacing: '0.16em',
          color: COLORS.accent,
        }}
      >
        Soul Map
      </p>
      {documentName && (
        <p
          style={{
            margin: 0,
            fontFamily: SERIF_FONT,
            fontSize: 19,
            lineHeight: '22px',
            color: COLORS.ink,
          }}
        >
          {documentName}
        </p>
      )}
      <div className="flex flex-col" style={{ marginTop: 8, gap: 6 }}>
        {['100%', '88%', '94%', '70%'].map((width) => (
          <div
            key={width}
            style={{
              height: 4,
              width,
              borderRadius: 2,
              background: COLORS.paperLine,
            }}
          />
        ))}
      </div>
      <p
        style={{
          margin: 'auto 0 0',
          fontSize: 9,
          letterSpacing: '0.06em',
          color: COLORS.muted,
        }}
      >
        Twelve sections · PDF
      </p>
    </div>

    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      className="absolute flex items-center justify-center"
      style={{
        top: 16,
        right: 16,
        width: 44,
        height: 44,
        border: 0,
        borderRadius: 9999,
        background: 'rgba(255, 255, 255, 0.7)',
        cursor: 'pointer',
      }}
    >
      <svg
        aria-hidden="true"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={COLORS.ink}
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  </div>
);

/** The connected dots behind the page; scales with the modal width. */
const Constellation = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 600 268"
    preserveAspectRatio="xMidYMid slice"
    className="absolute inset-0 h-full w-full"
  >
    <g stroke={COLORS.coral} strokeWidth="1" strokeOpacity="0.45" fill="none">
      <path d="M62 64 L128 102 L104 172 L176 208" />
      <path d="M128 102 L196 70" />
      <path d="M424 58 L486 96 L536 70" />
      <path d="M486 96 L470 174 L548 206" />
    </g>
    <g fill={COLORS.coral}>
      <circle cx="62" cy="64" r="3" />
      <circle cx="128" cy="102" r="4" />
      <circle cx="104" cy="172" r="2.5" />
      <circle cx="176" cy="208" r="3" />
      <circle cx="196" cy="70" r="2.5" />
      <circle cx="424" cy="58" r="2.5" />
      <circle cx="486" cy="96" r="4" />
      <circle cx="536" cy="70" r="3" />
      <circle cx="470" cy="174" r="3" />
      <circle cx="548" cy="206" r="2.5" />
    </g>
    <g fill={COLORS.violet} fillOpacity="0.7">
      <circle cx="34" cy="128" r="1.5" />
      <circle cx="232" cy="236" r="1.5" />
      <circle cx="380" cy="34" r="1.5" />
      <circle cx="572" cy="140" r="1.5" />
      <circle cx="250" cy="28" r="1.5" />
      <circle cx="352" cy="232" r="1.5" />
    </g>
  </svg>
);

const DownloadIcon = () => (
  <svg
    aria-hidden="true"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 4v11" />
    <path d="M7 10l5 5 5-5" />
    <path d="M5 20h14" />
  </svg>
);

export default SoulMapReadyModal;
